/**
 * EAA-1736: Floating layout toolbar for a selected media node (image/audio/embed).
 * Lets the author switch placement (inline / block / wrap / behind / front) and
 * remove the media. Mirrors the right-click context menu in MediaLayoutFrame, but
 * this surface has the toolbar `translate` fn so its labels are localised.
 */

import * as React from 'react';
import { Play, Trash2, Volume2 } from 'lucide-react';
import { useCurrentEditor, useEditorState } from '@tiptap/react';
import type { Editor } from '@tiptap/core';
import { BubbleMenu } from '@tiptap/react/menus';
import { circularDeepEqual } from 'fast-equals';
import { cn } from '../../../lib/utils';
import { kitT, type KitTranslateFn } from '../../../lib/kitTranslate';
import {
    DEFAULT_MEDIA_LAYOUT,
    isMediaLayout,
    type MediaLayout,
} from '../../../lib/tiptap-media-layout';
import type { AudioPlayerStyle } from '../../../lib/tiptap-audio';
import { ToolbarButton } from './ToolbarButton';
import { MEDIA_LAYOUT_OPTIONS } from './mediaLayoutOptions';

export interface MediaBubbleMenuProps {
    translate?: KitTranslateFn;
}

const MEDIA_TYPES = ['image', 'audio', 'embed'] as const;

function activeMediaType(editor: Editor): (typeof MEDIA_TYPES)[number] | null {
    for (const type of MEDIA_TYPES) {
        if (editor.isActive(type)) return type;
    }
    return null;
}

export const MediaBubbleMenu: React.FC<MediaBubbleMenuProps> = ({ translate }) => {
    const { editor } = useCurrentEditor();
    // WHY: Selection carries a circular reference to Node.type.schema; the
    // default plain deepEqual used by useEditorState recurses into that
    // cycle and throws "Maximum call stack size exceeded" on every selection
    // change (every keystroke), crashing the essay editor in the mobile
    // WebView. circularDeepEqual (fast-equals) is cycle-safe.
    useEditorState({
        editor,
        selector: (ctx) => ctx.editor?.state.selection ?? null,
        equalityFn: circularDeepEqual,
    });

    const tx = (k: string, fb: string) => kitT(translate, k, fb);

    if (!editor) return null;

    const type = activeMediaType(editor);
    const currentLayout: MediaLayout = (() => {
        if (!type) return DEFAULT_MEDIA_LAYOUT;
        const layout = editor.getAttributes(type).layout;
        return isMediaLayout(layout) ? layout : DEFAULT_MEDIA_LAYOUT;
    })();

    const setLayout = (layout: MediaLayout) => {
        const activeType = activeMediaType(editor);
        if (!activeType) return;
        editor.chain().focus().updateAttributes(activeType, { layout }).run();
    };

    // EAA-1736: audio-only — native controls vs a play-button-only "minimal" style.
    const currentAudioStyle: AudioPlayerStyle =
        type === 'audio' && editor.getAttributes('audio').style === 'minimal'
            ? 'minimal'
            : 'full';
    const setAudioStyle = (style: AudioPlayerStyle) => {
        editor.chain().focus().updateAttributes('audio', { style }).run();
    };

    return (
        <BubbleMenu
            editor={editor}
            pluginKey="mediaBubbleMenu"
            updateDelay={0}
            resizeDelay={0}
            className={cn(
                'rich-text-media-bubble-menu flex items-center gap-0.5 rounded-md border border-border bg-popover p-1 shadow-md'
            )}
            shouldShow={({ editor: ed }) =>
                ed.isEditable &&
                (ed.isActive('image') || ed.isActive('audio') || ed.isActive('embed'))
            }
        >
            {MEDIA_LAYOUT_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const label = tx(opt.labelKey, opt.labelFallback);
                return (
                    <ToolbarButton
                        key={opt.layout}
                        className="p-1.5"
                        isActive={currentLayout === opt.layout}
                        onClick={() => setLayout(opt.layout)}
                        aria-label={label}
                        title={label}
                    >
                        <Icon className="h-4 w-4" />
                    </ToolbarButton>
                );
            })}

            {type === 'audio' && (
                <>
                    <div className="rich-text-media-bubble-menu-separator w-px h-6 bg-border mx-0.5" aria-hidden />
                    <ToolbarButton
                        className="p-1.5"
                        isActive={currentAudioStyle === 'full'}
                        onClick={() => setAudioStyle('full')}
                        aria-label={tx('kRtmAudioStyleFull', 'Full controls')}
                        title={tx('kRtmAudioStyleFull', 'Full controls')}
                    >
                        <Volume2 className="h-4 w-4" />
                    </ToolbarButton>
                    <ToolbarButton
                        className="p-1.5"
                        isActive={currentAudioStyle === 'minimal'}
                        onClick={() => setAudioStyle('minimal')}
                        aria-label={tx('kRtmAudioStyleMinimal', 'Minimal (play button only)')}
                        title={tx('kRtmAudioStyleMinimal', 'Minimal (play button only)')}
                    >
                        <Play className="h-4 w-4" />
                    </ToolbarButton>
                </>
            )}

            <div className="rich-text-media-bubble-menu-separator w-px h-6 bg-border mx-0.5" aria-hidden />

            <ToolbarButton
                className="p-1.5"
                onClick={() => editor.chain().focus().deleteSelection().run()}
                aria-label={tx('kRtmRemoveMedia', 'Remove')}
                title={tx('kRtmRemoveMedia', 'Remove')}
            >
                <Trash2 className="h-4 w-4 text-destructive" />
            </ToolbarButton>
        </BubbleMenu>
    );
};

MediaBubbleMenu.displayName = 'MediaBubbleMenu';
